import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Reusable "Log to today's calories" button.
 *
 * Posts a kcal entry to /api/calorie-log and shows a toast with the remaining
 * daily budget. Auto-disables itself after a successful log so the same recipe
 * can't be double-logged accidentally (component-level only — refreshing the
 * page resets the state, which is fine).
 */
export default function LogKcalButton({ kcal, name, source = "manual", testid, className, protein_g, carbs_g, fat_g }) {
  const [busy, setBusy] = useState(false);
  const [logged, setLogged] = useState(false);

  if (kcal == null || Number.isNaN(Number(kcal))) return null;

  const log = async () => {
    setBusy(true);
    try {
      const payload = {
        name,
        kcal: Math.round(Number(kcal)),
        source,
      };
      if (protein_g != null) payload.protein_g = Number(protein_g);
      if (carbs_g != null) payload.carbs_g = Number(carbs_g);
      if (fat_g != null) payload.fat_g = Number(fat_g);
      const { data } = await api.post("/calorie-log", payload);
      setLogged(true);
      const remaining = data.remaining_kcal;
      const target = data.daily_target_kcal;
      if (target != null && remaining != null) {
        const left = Math.max(0, remaining);
        const over = remaining < 0 ? Math.abs(remaining) : 0;
        toast.success(
          over
            ? `+${data.kcal} kcal logged · ${over} over your ${target} target`
            : `+${data.kcal} kcal logged · ${left} kcal left today`,
          { duration: 5000 },
        );
      } else {
        toast.success(`+${data.kcal} kcal logged · set a target in Calories to track remaining`, { duration: 5000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log calories");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={log}
      disabled={busy || logged}
      data-testid={testid}
      className={className || `text-xs font-medium rounded-full px-3 py-2 transition disabled:opacity-60 ${
        logged
          ? "bg-[#E8F0E9] text-[#3B5A3E] border border-[#cfe0d0]"
          : "bg-[#2A332C] text-white hover:bg-[#1d2521]"
      }`}
      title={`Add ${Math.round(Number(kcal))} kcal to today`}
    >
      {logged ? "✓ Logged" : busy ? "Logging…" : `🍽️ +${Math.round(Number(kcal))} kcal`}
    </button>
  );
}
